import type { ParsedUrlQuery } from "querystring";

import type { AppliedRules } from "@/features/search/api/types";
import { rangeKeys } from "@/features/search/utils/search-params.utils";

/**
 * The current search as a `/search` query string, ready to POST to `/saved-searches`.
 *
 * Built from `meta.rules` — the recipe the service actually ran — rather than from the URL. On a
 * landing page most of the filters live in the *path* (`/furniture/sofas/3-5-seaters`), so a saved
 * search built from `location.search` would replay as a far wider result set than the one the
 * member was looking at. `rules` is the page's own filters already merged with the user's, which is
 * exactly what "save this search" means.
 *
 * View state is deliberately left out. Sort, order, page and lang describe how the results are
 * being looked at, not what was searched for — and the service drops them from `params` anyway, so
 * including them would only make two identical searches look different to the dedupe check.
 */
export const buildSavedSearchParams = (
  rules: AppliedRules | undefined,
  query: ParsedUrlQuery
): string => {
  const params = new URLSearchParams();

  const freeText = query.query;

  if (typeof freeText === "string" && freeText.trim()) {
    params.set("query", freeText.trim());
  }

  for (const [facetKey, values] of Object.entries(rules?.filters.terms ?? {})) {
    for (const value of values) {
      params.append(facetKey, value);
    }
  }

  for (const [facetKey, bounds] of Object.entries(
    rules?.filters.ranges ?? {}
  )) {
    const { min, max } = rangeKeys(facetKey);

    if (bounds.min != null) {
      params.set(min, String(bounds.min));
    }

    if (bounds.max != null) {
      params.set(max, String(bounds.max));
    }
  }

  return params.toString();
};

/**
 * Whether there is anything worth saving. An empty search matches the whole catalogue, so the
 * service rejects it with 422 — this is what hides the button rather than letting someone hit that.
 */
export const canSaveSearch = (params: string) => params.length > 0;
